import React, { useCallback, useMemo } from 'react';
import {
  bool, func, string, arrayOf,
} from 'prop-types';
import { Button } from '@picsio/ui';
import { useSelector } from 'react-redux';
import _get from 'lodash/get';

import picsioConfig from '../../../../../config';
import l18n from '../../shared/strings';
import { selectAccessLevel } from '../../store/selectors/teammates';
import useTeammatesNavigate from '../../helpers/components/hooks/useTeammatesNavigate';
import Logger from '../../services/Logger';
import { navigate } from '../../helpers/history';
import { showDialog } from '../dialog';
import isVisualSearchEnabled from '../../helpers/isVisualSearchEnabled';

const PlaceholderOtherFilters = ({
  isRootCollection = false, handleNewSearch = null, searchText = null, collectionNames = [],
}) => {
  const user = useSelector((state) => state.user);
  const accessLevel = useSelector(selectAccessLevel);
  const visualSearch = _get(user, 'subscriptionFeatures.visualSearch');
  const { adminEmails, onTeammateNavigateClick } = useTeammatesNavigate('tab=ai');

  const enableVisualSearchSetting = isVisualSearchEnabled(user);

  const onSendToIntercom = useCallback(async () => {
    Logger.log('User', 'VisualSearchRequestModalProceedToChatClicked', {
      bannerLocation: 'search_empty_state',
    });
    await window.Intercom('showNewMessage');
    setTimeout(() => {
      window.Intercom('private:sendMessage', l18n.DIALOGS.VISUAL_SEARCH.SUPPORT_MESSAGE);
    }, 2000);
  }, []);

  const adminRolesEmails = useMemo(() => (accessLevel === 'noAccess' ? adminEmails : ''), [accessLevel, adminEmails]);

  const onEnableVisualSearch = useCallback(() => {
    navigate('/teammates?tab=ai');
  }, []);

  const onCancel = useCallback(() => {
    Logger.log('User', 'VisualSearchRequestModalCancelClicked', {
      bannerLocation: 'search_empty_state',
    });
  }, []);

  const accessDialogConfigMap = useMemo(() => ({
    fullAccess: {
      textBtnCancel: l18n.DIALOGS.VISUAL_SEARCH.CANCEL_TEXT,
      textBtnOk: l18n.DIALOGS.VISUAL_SEARCH.OK_BUTTON_TEXTS.ENABLE_VISUAL_SEARCH,
      additionalTextBtn: l18n.DIALOGS.VISUAL_SEARCH.OK_BUTTON_TEXTS.PROCEED_TO_CHAT,
      onAdditionalClick: onSendToIntercom,
      onOk: onEnableVisualSearch,
      onCancel,
      onClose: onCancel,
    },
    partialAccess: {
      textBtnCancel: l18n.DIALOGS.VISUAL_SEARCH.CANCEL_TEXT,
      textBtnOk: l18n.DIALOGS.VISUAL_SEARCH.OK_BUTTON_TEXTS.PROCEED_TO_CHAT,
      onOk: onSendToIntercom,
      onCancel,
      onClose: onCancel,
    },
    noAccess: {
      textBtnCancel: null,
      textBtnOk: l18n.DIALOGS.VISUAL_SEARCH.OK_BUTTON_TEXTS.OK,
      onClose: onCancel,
    },
  }), [onCancel, onEnableVisualSearch, onSendToIntercom]);

  const accessDescriptionsMap = useMemo(() => ({
    fullAccess: l18n.DIALOGS.VISUAL_SEARCH.PERMISSION_TEXTS.FULL_ACCESS,
    partialAccess: l18n.DIALOGS.VISUAL_SEARCH.PERMISSION_TEXTS.PARTIAL_ACCESS,
    noAccess: l18n.DIALOGS.VISUAL_SEARCH.PERMISSION_TEXTS.NO_ACCESS,
  }), []);

  const onShowDialog = useCallback(() => {
    Logger.log('User', 'VisualSearchRequestModalOpened', {
      bannerLocation: 'search_empty_state',
    });

    showDialog({
      title: l18n.DIALOGS.VISUAL_SEARCH.TITLE,
      text: l18n.DIALOGS.VISUAL_SEARCH.TEXT(accessDescriptionsMap[accessLevel], adminRolesEmails),
      ...accessDialogConfigMap[accessLevel],
    });
  }, [accessDescriptionsMap, accessLevel, adminRolesEmails, accessDialogConfigMap]);

  const actionText = visualSearch ? l18n.VISUAL_SEARCH_BANNER.LIST_BANNER_ACTION_TEXT_ENABLE : l18n.VISUAL_SEARCH_BANNER.LIST_BANNER_ACTION_TEXT;

  const onCheckTeammatePermissions = useCallback(() => {
    Logger.log('User', 'VisualSearchBannerClicked', {
      bannerType: actionText,
      bannerLocation: 'search_empty_state',
    });

    onTeammateNavigateClick();
  }, [actionText, onTeammateNavigateClick]);

  const onVisualSearchRequest = useCallback(() => {
    if (visualSearch) {
      onCheckTeammatePermissions();
      return;
    }

    Logger.log('User', 'VisualSearchBannerClicked', {
      bannerType: actionText,
      bannerLocation: 'search_empty_state',
    });

    onShowDialog();
  }, [actionText, onCheckTeammatePermissions, onShowDialog, visualSearch]);

  const onContactSupport = useCallback(() => {
    Logger.log('User', 'SearchEmptyStateContactSupportClicked', {
      bannerLocation: 'search_empty_state',
    });

    if (window.Intercom) {
      window.Intercom('showNewMessage');
      return;
    }

    window.open(`mailto:${picsioConfig.isPim ? 'support@toriut.com' : 'support@pics.io'}`, '_blank');
  }, []);

  return (
    <div className="placeholderEmptyTitle">
      {l18n.SEARCH.noAssetsFound(collectionNames, visualSearch, onCheckTeammatePermissions, onVisualSearchRequest, enableVisualSearchSetting, onContactSupport)}

      <If condition={!isRootCollection && searchText}>
        <div className="searchInRoot">
          {l18n.SEARCH.searchInRoot}
        </div>
        <Button
          variant="contained"
          color="primary"
          size="md"
          onClick={handleNewSearch}
          style={{ display: 'inline' }}
        >
          {l18n.SEARCH.searchButtonText(searchText)}
        </Button>
      </If>
    </div>
  );
};

PlaceholderOtherFilters.propTypes = {
  isRootCollection: bool,
  handleNewSearch: func,
  searchText: string,
  collectionNames: arrayOf(string),
};

export default PlaceholderOtherFilters;
